import {ServiceBase} from '../ServiceBase';
import {
  IExternalLinksService,
  OpenContactUsOptions,
  OpenLinkParams,
  OpenMembershipResponse,
  OpenMyAccountOptions
} from './external-links.service.interface';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {OnPlayerActionParams} from '../sdk/sdk-events';
import {SdkOpenExternalLinkParams} from '../sdk/sdk.service.interface';
import {MarketingParams} from "../../../common/interfaces/marketing-params.interface";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";
import Device from "../../../sdk/src/utils/device/device";
import {GNWP_SOURCES} from "../../../sdk/src/components/external_links/external_links_consts";

export class ExternalLinksService extends ServiceBase implements IExternalLinksService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    this.services.sdk.events.onPlayerAction(this._onPlayerActionHandler.bind(this));
  }


  private _onPlayerActionHandler(params: OnPlayerActionParams) {
    if(params.actionName === 'deposit') {
      this.services.cashier.openCashier({
        ...params.options,
        channel: {
          source: CHANNEL_SOURCE.registration,
          area: AreaType.StraightToCashier,
          element: CHANNEL_ELEMENTS.depositButton
        }
      });
    }
  }

  openLink(params: OpenLinkParams, marketingParams?: MarketingParams, useCallbackURL: boolean = false): Promise<any> {
    let marketingData = this.services.marketing.getMarketingData(marketingParams);
    const isHybrid = Device.hybrid.isHybrid;


    const sdkOpenParams: SdkOpenExternalLinkParams = {
      view: params.view,
      source: params.source,
      sourceLink: params.sourceLink,
      marketing: marketingData,
      options: {...params.options, showRegister: !this.services.cmsSegmentation.isCisOrCip && this.services.configuration.data.generalSettings.showRegisterButton}
    };

    if(params.source === 'cashier') {
      return this.services.cashier.openCashier({
        ...sdkOpenParams.options,
        channel: {
          source: CHANNEL_SOURCE.casinoClient,
          area: AreaType.NavigationMenu,
          element: CHANNEL_ELEMENTS.depositButton
        }

      }, sdkOpenParams.marketing, useCallbackURL);
    }

    return new Promise((resolve, reject) => {
      if(params.resolveByCallback && !isHybrid) {
        sdkOpenParams.actionCallback = (result: any) => resolve(result);
      }

      this.services.sdk.openExternalLink(sdkOpenParams, useCallbackURL).then(()=> {
        // In case there is an actionCallback this promise will be resolved by a call from inside sdk.openExternalLink
        if(!sdkOpenParams.actionCallback) {
          resolve();
        }
      }).catch((err)=> {
        reject(err);
      });
    });
  }

  openResponsibleGamblingLink(view?:number):void{
    this.services.sdk.openResponsibleGamingLink(view);
  }


  async openMyAccount(options?: OpenMyAccountOptions, marketingParams?: MarketingParams, resolveByCallback: boolean = false): Promise<void> {
    await this.openLink({
      source: 'my_account',
      options: options,
      resolveByCallback: resolveByCallback
    }, marketingParams);
  }

  async openContactUs(options?: OpenContactUsOptions, marketingParams?: MarketingParams, resolveByCallback: boolean = false): Promise<void> {
    await this.openLink({
      source: 'contact_us',
      view: options?.view,
      resolveByCallback: resolveByCallback
    }, marketingParams);
  }

  async openOldChat(): Promise<void> {
    await this.openLink({
      source: 'live_chat',
      view: 'window'
    });
  }

  async openHelpPage(): Promise<void> {
    await this.openLink({
      source: 'help',
      view: 'window',
      resolveByCallback: false
    });
  }

  async openDownloadApp(): Promise<void> {
    await this.openLink({
      sourceLink: this.services.sdk.getDownloadAppLink(),
      view: 'redirect',
      resolveByCallback: false
    });
  }

  async openUploadDocument(marketingParams?: MarketingParams, resolveByCallback: boolean = false): Promise<void> {
    await this.openLink({
      source: 'upload_document',
      resolveByCallback: resolveByCallback
    }, marketingParams);
  }

  async openMembership(marketingParams?: MarketingParams, resolveByCallback: boolean = false): Promise<OpenMembershipResponse> {
    let response = await this.openLink({
      source: 'membership',
      resolveByCallback: resolveByCallback
    }, marketingParams);

    response = (response || {}).actionName;


    switch(response) {
      case 'play':
        return OpenMembershipResponse.play;
      case 'deposit':
        return OpenMembershipResponse.deposit;
      case 'close':
        return OpenMembershipResponse.close;
      default:
        this.services.logger.error(`Unknown response '${response}' received from openMembership`);
        return OpenMembershipResponse.unknown;
    }
  }

  async openPendingPlayApplication(token: string): Promise<void> {
    await this.openLink({
      source: GNWP_SOURCES.PENDING_PLAY,
      options: {
        token: token
      }
    });
  }

  async openSafeToPlay(): Promise<void> {
    const url = this.services.sdk.getSkin().getSafeToPlayURL();
    if(url) {
      await this.openLink({
        sourceLink: url,
        view: 'window'
      });
    }
  }
}
